import React from 'react';

import { RestrictionReason } from '@trello/enterprise';
import { avatarsFromAvatarUrl } from '@trello/members';
import { currentLocale } from '@trello/locale';
import { Tooltip, Button, usePopover, Popover } from '@trello/nachos';
import { EnterpriseDashboardTestIds } from '@trello/webdriverio-tests';
import { Spinner } from 'app/src/components/Spinner';

import { MemberAvatarUnconnected } from 'app/src/components/MemberAvatar';
import { forTemplate, localizeCount } from 'app/common/lib/util/i18n';
import { TeamLogo } from './TeamLogo';
import styles from './PendingTeamItem.less';
const format = forTemplate('enterprise_pending_teams');

interface PendingTeamMember {
  id: string;
  fullName: string;
  avatarUrl?: string;
  username: string;
  initials?: string;
}

interface PendingTeamRestrictedMember extends PendingTeamMember {
  reason: RestrictionReason;
}

export interface PendingTeam {
  id: string;
  displayName: string;
  requestorName?: string;
  requestDate: Date;
  logoUrl?: string;
  numMembers: number;
  newBillableMembers: PendingTeamMember[];
  restrictedMembers: PendingTeamRestrictedMember[];
}

interface PendingTeamItemProps extends PendingTeam {
  onApprove: () => void;
  onDecline: () => void;
  isLoading: boolean;
  enterprise: {
    totalMembers: number;
    maxMembers: number | null;
  };
}

export const PendingTeamItem = (props: PendingTeamItemProps) => {
  const {
    id,
    displayName,
    requestorName,
    requestDate,
    numMembers,
    logoUrl,
    newBillableMembers,
    restrictedMembers,
    onApprove,
    onDecline,
    isLoading,
    enterprise,
  } = props;

  const {
    popoverProps: newBillablePopoverProps,
    triggerRef: newBillableTriggerRef,
    toggle: newBillableToggle,
  } = usePopover<HTMLAnchorElement>();

  const {
    popoverProps: restrictedPopoverProps,
    triggerRef: restrictedTriggerRef,
    toggle: restrictedToggle,
  } = usePopover<HTMLAnchorElement>();

  const newBillableText = format('new-billable', {
    numNewBillable: newBillableMembers.length,
  });

  const restrictedText = format('restricted', {
    numRestricted: restrictedMembers.length,
  });

  const newBillableComponent =
    newBillableMembers.length > 0 ? (
      <a
        href="#"
        onClick={newBillableToggle}
        data-test-id={`${EnterpriseDashboardTestIds.NewBillableMembers}`}
        ref={newBillableTriggerRef}
      >
        {newBillableText}
      </a>
    ) : (
      <span data-test-id={`${EnterpriseDashboardTestIds.NewBillableMembers}`}>
        {newBillableText}
      </span>
    );

  const restrictedComponent =
    restrictedMembers.length > 0 ? (
      <a
        href="#"
        onClick={restrictedToggle}
        data-test-id={`${EnterpriseDashboardTestIds.RestrictedMembers}`}
        ref={restrictedTriggerRef}
      >
        {restrictedText}
      </a>
    ) : (
      <span data-test-id={`${EnterpriseDashboardTestIds.RestrictedMembers}`}>
        {restrictedText}
      </span>
    );

  const wouldExceedSeatCount = Boolean(
    newBillableMembers.length > 0 &&
      enterprise.maxMembers &&
      enterprise.totalMembers + newBillableMembers.length >
        enterprise.maxMembers,
  );

  let approveButton = (
    <Button
      onClick={onApprove}
      data-test-id={EnterpriseDashboardTestIds.PendingTeamItemApproveButton}
      isDisabled={wouldExceedSeatCount}
    >
      {format('approve')}
    </Button>
  );

  if (enterprise.maxMembers && wouldExceedSeatCount) {
    approveButton = (
      <Tooltip
        content={format('need-to-purchase-more-licenses', {
          numNewLicenses: localizeCount(
            'new licenses',
            enterprise.totalMembers +
              newBillableMembers.length -
              enterprise.maxMembers,
          ),
        })}
        tag="span"
      >
        {approveButton}
      </Tooltip>
    );
  }

  return (
    <>
      <div
        className={styles.pendingTeamItem}
        data-test-id={`${EnterpriseDashboardTestIds.PendingTeamItem}${id}`}
      >
        <TeamLogo className={styles.teamAvatar} size={32} url={logoUrl} />
        <div className={styles.requestInfo}>
          <span className={styles.teamName}>{displayName}</span>
          <span className={styles.requestor}>
            {requestorName
              ? format('requested-by', {
                  requestorName,
                  date: requestDate.toLocaleDateString(currentLocale, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }),
                })
              : format('requested-on', {
                  date: requestDate.toLocaleDateString(currentLocale, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }),
                })}
          </span>
        </div>
        <div className={styles.members}>
          <span>{format('members', { numMembers })}</span>
          <span className={styles.membersDetails}>
            {newBillableComponent},&ensp;{restrictedComponent}
          </span>
        </div>
        <div className={styles.actions}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              <Button
                onClick={onDecline}
                data-test-id={
                  EnterpriseDashboardTestIds.PendingTeamItemDeclineButton
                }
              >
                {format('decline')}
              </Button>
              {approveButton}
            </>
          )}
        </div>
      </div>
      <Popover
        {...newBillablePopoverProps}
        title={format('new-billable-members-popover-title')}
        noHorizontalPadding
      >
        <span
          data-test-id={`${EnterpriseDashboardTestIds.NewBillableMembersList}`}
        >
          <NewBillableMembersList members={newBillableMembers} />
        </span>
      </Popover>
      <Popover
        {...restrictedPopoverProps}
        title={format('restricted-members-popover-title')}
        noHorizontalPadding
      >
        <span
          data-test-id={`${EnterpriseDashboardTestIds.RestrictedMembersList}`}
        >
          <RestrictedMembersList members={restrictedMembers} />
        </span>
      </Popover>
    </>
  );
};

interface NewBillableMembersListProps {
  members: PendingTeamMember[];
}

const NewBillableMembersList: React.FunctionComponent<NewBillableMembersListProps> = ({
  members,
}: NewBillableMembersListProps) => {
  return (
    <div>
      {members.map((m) => (
        <div className={styles.member} key={m.id}>
          <span className={styles.memberAvatar}>
            <MemberAvatarUnconnected
              avatars={
                m.avatarUrl ? avatarsFromAvatarUrl(m.avatarUrl) : undefined
              }
              initials={m.initials}
            />
          </span>
          <span className={styles.memberInfo}>
            <p className={styles.fullName}>{m.fullName}</p>
            <p className={styles.username}>{m.username}</p>
          </span>
        </div>
      ))}
    </div>
  );
};

interface RestrictedMembersListProps {
  members: PendingTeamRestrictedMember[];
}

const RestrictedMembersList: React.FunctionComponent<RestrictedMembersListProps> = ({
  members,
}: RestrictedMembersListProps) => {
  return (
    <div>
      {members.map((m) => (
        <div
          className={styles.member}
          key={m.id}
          data-test-id={`${EnterpriseDashboardTestIds.PendingTeamItemRestrictedMember}${m.id}`}
        >
          <span className={styles.memberAvatar}>
            <MemberAvatarUnconnected
              avatars={
                m.avatarUrl ? avatarsFromAvatarUrl(m.avatarUrl) : undefined
              }
              initials={m.initials}
            />
          </span>
          <span className={styles.memberInfo}>
            <p className={styles.fullName}>{m.fullName}</p>
            <p className={styles.username}>{m.username}</p>
            {m.reason === RestrictionReason.DeactivatedInEnterprise && (
              <p
                className={styles.badge}
                data-test-id={
                  EnterpriseDashboardTestIds.PendingTeamItemRestrictedMemberDeactivatedBadge
                }
              >
                {format('deactivated')}
              </p>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};
